// AdministrativeAreaMoveDrawer — the "Move" reparent surface (the area under the title), on the shared DrawerForm +
// useFormShell (RHF + Zod): the NEW parent on the navigator field (ADR-080 §14), the same
// field as Add's, restricted to the area's own country (its name picks the country's top
// level) and LEAVING OUT the area and everything inside it, so no cycle is offerable. The
// backend re-guards (same country · no cycle) and its refusal reads back in the form.
// Its rows are built here over the page's own tree, as Add's are (`parentRows`).
// The move rides the row stamp (ADR-084) — the MOVED area's own (its parent is a field of
// its row): the shell seeds from where the area sits at each open, and a stale save shows
// the line with Load the latest — the tree re-read, the area picked from it.

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { DrawerForm } from '../../../components/patterns/forms/DrawerForm.js';
import { readLatestRow } from '../../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../../components/patterns/forms/useFormShell.js';
import { Picker } from '../../../components/patterns/Picker.js';
import { useMoveAdministrativeArea } from '../../../hooks/useAdministrativeAreaMutations.js';
import { administrativeAreasKeys, fetchAdministrativeAreas } from '../../../hooks/useAdministrativeAreas.js';
import type { CodeSource } from '../../../hooks/useOptions.js';
import { countryName } from '../../../lib/format.js';
import { COUNTRY_VALUE, parentRows } from './administrativeAreaTree.js';
import type { AdministrativeAreaNode } from '@ems-app/schemas/administrative-areas/responses';

const schema = z.object({ parentValue: z.string().min(1) });
type Values = z.infer<typeof schema>;

export interface AdministrativeAreaMoveDrawerProps {
  projectId: string;
  allNodes: AdministrativeAreaNode[];
  node: AdministrativeAreaNode | null;
  open: boolean;
  onClose: () => void;
}

/** Where an area sits, as the parent field's value. */
const placeOf = (node: AdministrativeAreaNode | null): string =>
  node == null ? '' : node.parent_id == null ? `${COUNTRY_VALUE}${node.country_iso_code}` : String(node.parent_id);

export function AdministrativeAreaMoveDrawer({ projectId, allNodes, node, open, onClose }: AdministrativeAreaMoveDrawerProps) {
  const { t, i18n } = useTranslation('administrative-areas');
  const lang = i18n.language;
  const move = useMoveAdministrativeArea(projectId);
  const qc = useQueryClient();

  const source = useMemo<CodeSource>(
    () => ({
      kind: 'code',
      key: 'administrative-area-move',
      rows: node
        ? parentRows(allNodes, { countries: [node.country_iso_code], countryLabel: (iso) => countryName(iso, lang), excludeSubtreeOf: node.id })
        : [],
    }),
    [allNodes, node, lang],
  );

  const shell = useFormShell<Values, AdministrativeAreaNode>({
    schema,
    row: node,
    values: (r) => ({ parentValue: placeOf(r) }),
    reload: node
      ? () => readLatestRow(qc, administrativeAreasKeys.all(projectId), () => fetchAdministrativeAreas(projectId), (l) => l.find((n) => n.id === node.id))
      : undefined,
    onSubmit: async (v, { stamp, changed }) => {
      // An unchanged parent (since the drawer opened) moves nothing.
      if (!node || !('parentValue' in changed)) return;
      const newParentId = v.parentValue.startsWith(COUNTRY_VALUE) ? null : Number(v.parentValue);
      await move.mutateAsync({ administrativeAreaId: node.id, body: { row_version: stamp, new_parent_id: newParentId } });
    },
    onSuccess: onClose,
  });

  if (!node) return null;
  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('actions.move')} description={node?.name} submitLabel={t('form.moveConfirm')} maxWidth="max-w-md">
      <Controller
        control={shell.form.control}
        name="parentValue"
        render={({ field }) => (
          <Picker
            name="parentValue"
            source={source}
            navigator={{ thisLevel: t('form.thisArea') }}
            label={t('form.moveLabel')}
            searchPlaceholder={t('form.parentSearch')}
            required
            value={field.value || null}
            onChange={(v) => field.onChange(v ?? '')}
            error={shell.form.formState.errors.parentValue ? ' ' : undefined}
          />
        )}
      />
    </DrawerForm>
  );
}
